import TrashIcon from '@Resources/assets/icons/dark/trash.svg';
import { isValidEmail } from '@Utils/helpers/stringHelpers';
import { ITableEditOption, ITableEntity } from '@Utils/helpers/tableHelpers';
import { CircularProgress, SvgIcon } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import _isNil from 'lodash/isNil';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Button from '@Components/Button/Button';
import CircularLoading from '@Components/CircularLoading/CircularLoading';
import DeleteDialog from '@Components/DeleteDialog/DeleteDialog';
import TextInput from '@Components/DomElements/TextInput';
import IconButton from '@Components/IconButton/IconButton';
import SettingsTable, {
    SettingsTableViewState,
} from '@Components/Settings/components/SettingsTable/SettingsTable';
import SettingsTableButton from '@Components/Settings/components/SettingsTable/SettingsTableButton';
import Styles from '@Components/Settings/views/SettingsViews.scss';
import OrganizationsApi from '@Data/organizations/Api';
import { IUpdateOrgMember } from '@Data/organizations/Interfaces';
import {
    organizationMembersState,
    organizationsState,
    selectedOrganizationState,
} from '@Data/organizations/Reducer';
import { IUser } from '@Data/users/Interfaces';
import { ISettingsBaseComponentProps } from '../../Interfaces';

const emptyUpdateOrgMember = {
    organisationId: '',
    email: '',
    name: '',
    nickname: '',
};

const OrgMembersTable = (props: ISettingsBaseComponentProps) => {
    const { title, isSubmitting, setIsSubmitting, id } = props;
    const org = useSelector(selectedOrganizationState);
    const orgMembers = useSelector(organizationMembersState);
    const [selectedUser, setSelectedUser] = useState<IUser>(null);
    const [tableState, setTableState] = useState<SettingsTableViewState>(
        SettingsTableViewState.VIEW
    );
    const [updateUserEntity, setUpdateUserEntity] =
        useState<IUpdateOrgMember>(emptyUpdateOrgMember);
    const [deleteDialogIsOpen, setDeleteDialogIsOpen] =
        useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        handleGetOrgMembers();
    }, []);

    const handleGetOrgMembers = async () => {
        setIsLoading(true);
        await OrganizationsApi.getOrganizationMembers(org.organisation_id);
        setUpdateUserEntity({
            ...emptyUpdateOrgMember,
            organisationId: org.organisation_id,
        });
        setIsLoading(false);
    };

    const columnHelper = createColumnHelper<IUser>();
    const columns = [
        columnHelper.accessor('email', {
            cell: (info) => info.getValue(),
            header: 'Member IKE ID',
        }),
        columnHelper.accessor('nickname', {
            cell: (info) => info.getValue(),
            header: 'Name',
        }),
        columnHelper.accessor('actions', {
            cell: null,
            header: 'Actions',
            enableSorting: false,
        }),
    ];

    const submitCleanup = async () => {
        handleCancelUpdateUser();
        setUpdateUserEntity(emptyUpdateOrgMember);
        setSelectedUser(null);
        setIsSubmitting(false);
    };

    const handleUpdateUser = async () => {
        setIsSubmitting(true);

        if (tableState === SettingsTableViewState.CREATE) {
            await OrganizationsApi.createOrgMember(
                updateUserEntity.organisationId,
                updateUserEntity.email
            );
        } else if (tableState === SettingsTableViewState.EDIT) {
            await OrganizationsApi.deleteOrgMember(
                updateUserEntity.organisationId,
                updateUserEntity.email
            );
        }
        submitCleanup();
        await handleGetOrgMembers();
    };

    const handleSetEditState = () => {
        setUpdateUserEntity({
            organisationId: org.organisation_id,
            email: selectedUser.email,
            name: selectedUser.name,
            nickname: selectedUser.nickname,
        });
        setTableState(SettingsTableViewState.EDIT);
    };

    const editCells: Array<ITableEditOption> = [
        { attribute: 'email', component: <TextInput disabled={true} /> },
        { attribute: 'nickname', component: <TextInput disabled={true} /> },
        {
            attribute: 'actions',
            isStandalone: true,
            component: (
                <IconButton
                    onClick={() => {
                        setUpdateUserEntity({
                            organisationId: org.organisation_id,
                            name: selectedUser.name,
                            email: selectedUser.email,
                            nickname: selectedUser.nickname,
                        });
                        setDeleteDialogIsOpen(true);
                    }}
                    style={{ backgroundColor: 'transparent' }}
                    icon={
                        <SvgIcon inheritViewBox>
                            <TrashIcon />
                        </SvgIcon>
                    }
                />
            ),
        },
    ];

    const createCells: Array<ITableEditOption> = [
        { attribute: 'email', component: <input type="text" /> },
        { attribute: 'nickname', component: null },
        { attribute: 'actions', component: null },
    ];

    const handleCancelUpdateUser = () => {
        setTableState(SettingsTableViewState.VIEW);
        setUpdateUserEntity(emptyUpdateOrgMember);
    };

    const canCreateNewEntity =
        tableState === SettingsTableViewState.CREATE &&
        isValidEmail(updateUserEntity.email);

    return (
        <div className={Styles.settingsViewWrapper}>
            <div className={Styles.settingsViewHeader}>
                <span id={`${id}-title`}>{title}</span>

                <div>
                    {tableState !== SettingsTableViewState.VIEW && (
                        <Button
                            id={`cancel-edit-${id}-btn`}
                            color={'black'}
                            onClick={handleCancelUpdateUser}
                        >
                            Cancel
                        </Button>
                    )}

                    <SettingsTableButton
                        id={`edit-${id}-table-btn`}
                        tableState={tableState}
                        canEdit={!_isNil(selectedUser)}
                        createText={'Add New Member'}
                        editText={'Edit Member'}
                        setCreateState={() =>
                            setTableState(SettingsTableViewState.CREATE)
                        }
                        setEditState={handleSetEditState}
                        onCreateNewEntity={handleUpdateUser}
                        onEditEntity={handleUpdateUser}
                        canCreateNewEntity={canCreateNewEntity}
                        isSubmitting={isSubmitting}
                        hideEditButton
                    />
                </div>
            </div>
            <div className={Styles.tableContainer}>
                {isLoading ? (
                    <CircularLoading />
                ) : (
                    <SettingsTable
                        id={`${id}-table`}
                        columns={columns}
                        data={orgMembers}
                        onRowClick={(user) => setSelectedUser(user)}
                        selectedRow={selectedUser}
                        state={tableState}
                        updateEntity={updateUserEntity}
                        onChangeUpdateEntity={setUpdateUserEntity}
                        editCells={editCells}
                        createCells={createCells}
                        isSubmitting={isSubmitting}
                    />
                )}
                <DeleteDialog
                    id={`${id}-delete-dialog`}
                    isOpen={deleteDialogIsOpen}
                    onClose={() => setDeleteDialogIsOpen(false)}
                    onDelete={() => {
                        handleUpdateUser();
                        setDeleteDialogIsOpen(false);
                    }}
                    entityName={selectedUser?.email}
                    isDeleting={isSubmitting}
                />
            </div>
        </div>
    );
};

export default OrgMembersTable;
